import { Fragment } from 'react';
import { Badge } from '@/components/ui/badge';
import { TooltipProvider } from '@/components/ui/tooltip';
import { type EventType } from '@/lib/constants';
import { getTimeDiff, formatDuration } from '@/lib/utils';
import { TimelineEvent } from './TimelineEvent';
import { TimelineConnector } from './TimelineConnector';

interface Event {
  id: string;
  eventType: EventType;
  pageUrl?: string;
  occurredAt: string;
  eventData?: Record<string, unknown>;
}

interface Session {
  id: string;
  clientId: string;
  customerId?: string;
  distributorId?: string;
  events: Event[];
  startedAt: string;
  converted: boolean;
  convertedAt?: string;
  orderId?: number;
}

interface JourneyTimelineProps {
  session: Session;
  showSummary?: boolean;
}

export function JourneyTimeline({ session, showSummary = true }: JourneyTimelineProps) {
  const sortedEvents = [...session.events].sort(
    (a, b) => new Date(a.occurredAt).getTime() - new Date(b.occurredAt).getTime()
  );

  const duration = session.convertedAt
    ? formatDuration(new Date(session.convertedAt).getTime() - new Date(session.startedAt).getTime())
    : formatDuration(new Date().getTime() - new Date(session.startedAt).getTime());

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {showSummary && (
          <div className="flex items-center gap-4 pb-3 border-b">
            <div>
              <p className="text-xs text-muted-foreground">Session ID</p>
              <p className="font-mono text-xs">{session.id.slice(0, 8)}...</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Duration</p>
              <p className="text-sm font-medium">{duration}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Events</p>
              <p className="text-sm font-medium">{sortedEvents.length}</p>
            </div>
            {session.converted && (
              <Badge variant="success" className="ml-auto">
                Converted
              </Badge>
            )}
          </div>
        )}

        <div className="relative pl-1">
          {sortedEvents.map((event, index) => {
            const prevEvent = sortedEvents[index - 1];
            const timeDiff = prevEvent ? getTimeDiff(prevEvent.occurredAt, event.occurredAt) : null;
            const isConversion = event.eventType === 'checkout_completed';
            const isFirst = index === 0;
            const isLast = index === sortedEvents.length - 1;

            return (
              <Fragment key={event.id}>
                {timeDiff && timeDiff !== '0s' && <TimelineConnector duration={timeDiff} />}
                <TimelineEvent
                  event={event}
                  isConversion={isConversion}
                  isFirst={isFirst}
                  isLast={isLast}
                />
              </Fragment>
            );
          })}
        </div>

        {sortedEvents.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            No events recorded for this session
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
